<template>
  <div>
    <b-card no-body>
      <b-card-header class="d-flex flex-wrap justify-content-between align-items-center">
        <b-form-radio-group
          v-model="filter"
          :options="filterOptions"
          buttons
          button-variant="outline-primary"
          size="sm"
          @change="onFilterChange"
        />
        <div class="d-flex align-items-center mt-1 mt-md-0">
          <small
            v-if="lastLoaded"
            class="text-muted mr-1"
          >Updated {{ lastLoaded }}</small>
          <b-button
            size="sm"
            variant="outline-secondary"
            :disabled="loading"
            @click="load"
          >
            <feather-icon icon="RefreshCwIcon" />
            Refresh
          </b-button>
        </div>
      </b-card-header>

      <b-alert
        :show="!!error"
        variant="danger"
        class="m-1"
      >
        <div class="alert-body">
          {{ error }}
        </div>
      </b-alert>

      <b-table
        hover
        responsive
        show-empty
        empty-text="Nothing here"
        :items="items"
        :fields="fields"
        :busy="loading"
        class="mb-0"
        @row-clicked="openItem"
      >
        <template #cell(created_at)="data">
          {{ formatTime(data.value) }}
        </template>
        <template #cell(from_addr)="data">
          <div class="font-weight-bold">
            {{ data.item.from_name || data.value }}
          </div>
          <small
            v-if="data.item.from_name"
            class="text-muted"
          >{{ data.value }}</small>
        </template>
        <template #cell(category)="data">
          <span v-if="data.value">{{ data.value.replace(/_/g, ' ') }}</span>
          <span
            v-else
            class="text-muted"
          >-</span>
        </template>
        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.value)">
            {{ data.value.replace(/_/g, ' ') }}
          </b-badge>
        </template>
        <template #cell(status_reason)="data">
          <small class="text-muted">{{ truncate(data.value, 90) }}</small>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BTable, BBadge, BButton, BAlert, BFormRadioGroup,
} from 'bootstrap-vue'
import supportApi from '@/libs/support-api'

const REFRESH_MS = 60000

export default {
  components: {
    BCard, BCardHeader, BTable, BBadge, BButton, BAlert, BFormRadioGroup,
  },
  data() {
    return {
      filter: 'drafted,needs_human',
      filters: [
        { text: 'To review', value: 'drafted,needs_human' },
        { text: 'Drafted', value: 'drafted' },
        { text: 'Needs human', value: 'needs_human' },
        { text: 'Errors', value: 'error' },
        { text: 'Skipped', value: 'skipped' },
        { text: 'Done', value: 'sent,dismissed,answered' },
        { text: 'All', value: '' },
      ],
      fields: [
        { key: 'id', label: '#' },
        { key: 'created_at', label: 'Received' },
        { key: 'from_addr', label: 'From' },
        { key: 'subject', label: 'Subject' },
        { key: 'category', label: 'Topic' },
        { key: 'status', label: 'Status' },
        { key: 'status_reason', label: 'Why' },
      ],
      counts: {},
      items: [],
      loading: false,
      error: '',
      lastLoaded: '',
      timer: null,
    }
  },
  computed: {
    filterOptions() {
      const c = this.counts
      const n = (...keys) => keys.reduce((sum, k) => sum + (c[k] || 0), 0)
      const total = Object.keys(c).reduce((sum, k) => sum + c[k], 0)
      const amounts = {
        'drafted,needs_human': n('drafted', 'needs_human'),
        drafted: n('drafted'),
        needs_human: n('needs_human'),
        error: n('error'),
        skipped: n('skipped'),
        'sent,dismissed,answered': n('sent', 'dismissed', 'answered'),
        '': total,
      }
      return this.filters.map(f => ({ ...f, text: amounts[f.value] ? `${f.text} (${amounts[f.value]})` : f.text }))
    },
  },
  created() {
    this.load()
    this.timer = setInterval(this.load, REFRESH_MS)
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  methods: {
    onFilterChange(value) {
      this.filter = value
      this.load()
    },
    loadCounts() {
      supportApi('get', '/stats')
        .then(response => {
          this.counts = response.data
        })
        .catch(() => {})
    },
    load() {
      this.loadCounts()
      this.loading = true
      const query = this.filter ? `?status=${encodeURIComponent(this.filter)}` : ''
      supportApi('get', `/items${query}`)
        .then(response => {
          this.items = response.data
          this.error = ''
          this.lastLoaded = new Date().toLocaleTimeString()
        })
        .catch(error => {
          this.error = error.message
        })
        .then(() => {
          this.loading = false
        })
    },
    openItem(item) {
      this.$router.push(`/support/${item.id}`)
    },
    statusVariant(status) {
      return {
        drafted: 'success',
        needs_human: 'warning',
        error: 'danger',
        sent: 'primary',
        dismissed: 'secondary',
        answered: 'info',
        skipped: 'light',
        new: 'info',
      }[status] || 'secondary'
    },
    formatTime(seconds) {
      return seconds ? new Date(seconds * 1000).toLocaleString() : ''
    },
    truncate(text, length) {
      if (!text) {
        return ''
      }
      return text.length > length ? `${text.substring(0, length)}...` : text
    },
  },
}
</script>
